import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Search } from 'react-feather';
import {
  BuyerStatementData,
  QuickbooksInvoice,
  QuickbooksCreditMemo,
  QuickbooksJob,
  SupplierInvoices,
  formatMoney,
  removeDuplicatesByProperty,
  isCreditMemoSelectable,
} from '@dill/dill-shared';
import {
  CaretDown,
  FilePdf,
  DownloadSimple,
  Buildings,
  Spinner,
  Info,
  X,
} from '@phosphor-icons/react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getStatementDetails,
  getJobFilteredStatementPdf,
} from '../../../../redux/services/buyersService';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { COLORS } from '../../../../utils/colors';
import { truncateText, openDillToast } from '../../../../utils/helpers';
import { DillButton, DillCheckbox, DillDropdown } from '../../../general/Dill';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import AppSelectedPayInvoicesModal from '../../../general/AppSelectedPayInvoicesModal/AppSelectedPayInvoicesModal';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { AppCheckBox } from '../../../general/AppCheckBox/AppCheckBox';
import StatementExpandableTable from './StatementExpandableTable';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';
import { Eye } from 'phosphor-react';
import { useBuyerUserPermissions } from '../../../../utils/hooks/useBuyerUserPermissions';
import { Tooltip as ReactTooltip } from 'react-tooltip';

interface StatementPeriodRowProps {
  statementData: BuyerStatementData;
  onNavigate: () => void;
}

const StatementPeriodRow = ({ statementData, onNavigate }: StatementPeriodRowProps) => {
  const dispatch = useAppDispatch();
  const [mainBuyerId] = useParamMainBuyerId();
  const { selectedBuyerSuppliers, selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const { buyerUserCanPay } = useBuyerUserPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const isExpanded = searchParams.get('statementId') === statementData.id;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [liveInvoices, setLiveInvoices] = useState<QuickbooksInvoice[]>([]);
  const [liveCreditMemos, setLiveCreditMemos] = useState<QuickbooksCreditMemo[]>([]);
  const [payInvoicesModalOpen, setPayInvoicesModalOpen] = useState(false);
  const [invoicesToBePaid, setInvoicesToBePaid] = useState<QuickbooksInvoice[]>([]);
  const [creditMemosToApply, setCreditMemosToApply] = useState<QuickbooksCreditMemo[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [pendingCreditMemoIds, setPendingCreditMemoIds] = useState<string[]>([]);
  const [groupedInvoices, setGroupedInvoices] = useState<SupplierInvoices | null>(null);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [loadingJobPdfDownload, setLoadingJobPdfDownload] = useState(false);
  const [loadingJobPdfPreview, setLoadingJobPdfPreview] = useState(false);
  const [jobPdfBlobUrl, setJobPdfBlobUrl] = useState<string | null>(null);
  const formattedDate = moment(statementData.statementDate).format('MMMM D, YYYY');
  const statementFileUrl = statementData.statementFile?.url || null;

  const supplier = useMemo(
    () => (selectedBuyerSuppliers.length > 0 ? selectedBuyerSuppliers[0] : null),
    [selectedBuyerSuppliers]
  );

  const selectedSupplierJobs = useMemo(() => {
    const jobs: QuickbooksJob[] = [...liveInvoices, ...liveCreditMemos].reduce<QuickbooksJob[]>(
      (prev, curr) => {
        if (curr.jobId && curr.quickBooksJob) {
          return [...prev, curr.quickBooksJob];
        }
        return prev;
      },
      []
    );
    return removeDuplicatesByProperty({ inputArray: jobs, property: 'id' });
  }, [liveInvoices, liveCreditMemos]);

  const activeJobCount = useMemo(
    () => selectedJobIds.filter((id) => id !== 'ALL').length,
    [selectedJobIds]
  );

  const activeJobs = useMemo(
    () => selectedSupplierJobs.filter((j) => selectedJobIds.includes(j.id)),
    [selectedSupplierJobs, selectedJobIds]
  );

  const filteredInvoices = useMemo(() => {
    if (!searchText && selectedJobIds.length === 0) return statementData.invoices;
    return statementData.invoices.filter((inv) => {
      const liveInv = liveInvoices.find((li) => li.id === inv.id);
      if (searchText) {
        const q = searchText.toLowerCase();
        const matchesText =
          (liveInv?.quickBooksJob?.jobName &&
            liveInv.quickBooksJob.jobName.toLowerCase().includes(q)) ||
          (liveInv?.quickBooksJob?.address &&
            liveInv.quickBooksJob.address.toLowerCase().includes(q)) ||
          (liveInv?.quickBooksJob?.jobNumber &&
            liveInv.quickBooksJob.jobNumber.toLowerCase().includes(q)) ||
          (liveInv?.poNumber && liveInv.poNumber.toLowerCase().includes(q)) ||
          (liveInv?.amount != null && Number(liveInv.amount).toFixed(2).includes(q)) ||
          (liveInv?.terms && liveInv.terms.toLowerCase().includes(q)) ||
          (liveInv?.dueDate &&
            moment(liveInv.dueDate).format('MM/DD/YYYY').toLowerCase().includes(q)) ||
          (liveInv?.createdAt &&
            moment(liveInv.createdAt).format('MM/DD/YYYY').toLowerCase().includes(q)) ||
          (inv.docNumber && inv.docNumber.toLowerCase().includes(q)) ||
          (liveInv?.docNumber && liveInv.docNumber.toLowerCase().includes(q)) ||
          (liveInv?.balance != null && Number(liveInv.balance).toFixed(2).includes(q)) ||
          (liveInv?.recordType && liveInv.recordType.toLowerCase().replace('_', ' ').includes(q));
        if (!matchesText) return false;
      }
      if (selectedJobIds.length > 0) {
        const invJobId = liveInv?.quickBooksJob?.id ?? liveInv?.jobId;
        if (!invJobId || !selectedJobIds.includes(invJobId)) return false;
      }
      return true;
    });
  }, [statementData.invoices, liveInvoices, searchText, selectedJobIds]);

  const filteredCreditMemos = useMemo(() => {
    if (!searchText && selectedJobIds.length === 0) return statementData.creditMemos;
    return statementData.creditMemos.filter((cm) => {
      const liveCm = liveCreditMemos.find((lcm) => lcm.id === cm.id);
      if (searchText) {
        const q = searchText.toLowerCase();
        const matchesText =
          (liveCm?.quickBooksJob?.jobName &&
            liveCm.quickBooksJob.jobName.toLowerCase().includes(q)) ||
          (liveCm?.quickBooksJob?.address &&
            liveCm.quickBooksJob.address.toLowerCase().includes(q)) ||
          (liveCm?.quickBooksJob?.jobNumber &&
            liveCm.quickBooksJob.jobNumber.toLowerCase().includes(q)) ||
          (cm.docNumber && cm.docNumber.toLowerCase().includes(q)) ||
          (liveCm?.docNumber && liveCm.docNumber.toLowerCase().includes(q)) ||
          (liveCm?.totalAmount != null && Number(liveCm.totalAmount).toFixed(2).includes(q)) ||
          (liveCm?.jobName && liveCm.jobName.toLowerCase().includes(q)) ||
          (liveCm?.createdAt &&
            moment(liveCm.createdAt).format('MM/DD/YYYY').toLowerCase().includes(q)) ||
          (liveCm?.remainingCredit != null &&
            Number(liveCm.remainingCredit).toFixed(2).includes(q)) ||
          (liveCm?.recordType && liveCm.recordType.toLowerCase().replace('_', ' ').includes(q));
        if (!matchesText) return false;
      }
      if (selectedJobIds.length > 0) {
        if (!liveCm?.quickBooksJob || !selectedJobIds.includes(liveCm.quickBooksJob.id))
          return false;
      }
      return true;
    });
  }, [statementData.creditMemos, liveCreditMemos, searchText, selectedJobIds]);

  const filteredLiveInvoices = useMemo(
    () => liveInvoices.filter((li) => filteredInvoices.some((fi) => fi.id === li.id)),
    [liveInvoices, filteredInvoices]
  );

  const filteredLiveCreditMemos = useMemo(
    () => liveCreditMemos.filter((lcm) => filteredCreditMemos.some((fc) => fc.id === lcm.id)),
    [liveCreditMemos, filteredCreditMemos]
  );

  // Recompute original balance based on filtered invoices/credit memos (using saved balances)
  const filteredOriginalBalance = useMemo(() => {
    const invTotal = filteredInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
    const cmTotal = filteredCreditMemos.reduce((sum, cm) => sum + (cm.remainingCredit ?? 0), 0);
    return invTotal - cmTotal;
  }, [filteredInvoices, filteredCreditMemos]);

  // Revoke stale blob URL whenever the job filter changes
  useEffect(() => {
    setJobPdfBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [selectedJobIds]);

  // Re-select only the selectable items within the current filtered set when the job filter changes
  useEffect(() => {
    if (liveInvoices.length === 0 && liveCreditMemos.length === 0) return;
    const selectableInvoiceIds = filteredInvoices
      .filter((inv) => {
        const liveInv = liveInvoices.find((li) => li.id === inv.id);
        return (
          !(liveInv && liveInv.balance === 0) &&
          liveInv?.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
          liveInv?.isDeleted !== true
        );
      })
      .map((inv) => inv.id);
    const selectableCmIds = filteredCreditMemos
      .filter((cm) => {
        const liveCm = liveCreditMemos.find((lcm) => lcm.id === cm.id);
        return (
          !pendingCreditMemoIds.includes(cm.id) &&
          !!liveCm &&
          liveCm.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
          isCreditMemoSelectable(liveCm?.recordType, supplier?.integrationType)
        );
      })
      .map((cm) => cm.id);
    setSelectedIds(new Set([...selectableInvoiceIds, ...selectableCmIds]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJobIds]);

  useEffect(() => {
    if (supplier) {
      dispatch(updateSelectedSupplierForBuyer(supplier));
    }
  }, [supplier]);

  const handlePayClick = useCallback(() => {
    const selectedLiveInvoices = liveInvoices.filter(
      (inv) =>
        selectedIds.has(inv.id) &&
        (inv.balance ?? 0) > 0 &&
        inv.dillPaymentStatus !== 'TRANSACTION_PENDING'
    );
    const selectedLiveCredits = liveCreditMemos.filter(
      (cm) =>
        selectedIds.has(cm.id) &&
        !pendingCreditMemoIds.includes(cm.id) &&
        cm.dillPaymentStatus !== 'TRANSACTION_PENDING' &&
        isCreditMemoSelectable(cm.recordType, supplier?.integrationType)
    );
    setInvoicesToBePaid(selectedLiveInvoices);
    setCreditMemosToApply(selectedLiveCredits);
    setPayInvoicesModalOpen(true);
  }, [liveInvoices, liveCreditMemos, selectedIds, supplier?.integrationType, pendingCreditMemoIds]);

  const selectableIds = useMemo(() => {
    const selectableInvoiceIds = statementData.invoices
      .filter((inv) => {
        const liveInv = liveInvoices.find((li) => li.id === inv.id);
        const isPaid = liveInv && liveInv.balance === 0;
        const isTransactionPending = liveInv?.dillPaymentStatus === 'TRANSACTION_PENDING';
        const isDeleted = liveInv?.isDeleted === true;
        return !isPaid && !isTransactionPending && !isDeleted;
      })
      .map((inv) => inv.id);
    const selectableCmIds = statementData.creditMemos
      .filter((cm) => {
        const liveCm = liveCreditMemos.find((lcm) => lcm.id === cm.id);
        const isPending = pendingCreditMemoIds.includes(cm.id);
        const isUsed = !liveCm;
        const isTransactionPending = liveCm?.dillPaymentStatus === 'TRANSACTION_PENDING';
        return (
          !isPending &&
          !isUsed &&
          !isTransactionPending &&
          isCreditMemoSelectable(liveCm?.recordType, supplier?.integrationType)
        );
      })
      .map((cm) => cm.id);
    return [...selectableInvoiceIds, ...selectableCmIds];
  }, [
    statementData,
    liveInvoices,
    liveCreditMemos,
    supplier?.integrationType,
    pendingCreditMemoIds,
  ]);

  const handleOpenPdfPreview = useCallback(() => {
    if (!statementFileUrl) return;
    setPdfPreviewOpen(true);
  }, [statementFileUrl]);

  const handleClosePdfPreview = useCallback(() => {
    setPdfPreviewOpen(false);
  }, []);

  const handleDownloadStatementPdf = useCallback(
    (url: string) => {
      if (!url) return;
      const link = document.createElement('a');
      link.href = url;
      link.download = `statement-${statementData.id}.pdf`;
      link.click();
    },
    [statementData.id]
  );

  const buildJobFilteredPdfUrl = useCallback(async (): Promise<string | null> => {
    if (!mainBuyerId) return null;
    const invoiceIds = filteredInvoices.map((inv) => inv.id);
    const creditMemoIds = filteredCreditMemos.map((cm) => cm.id);
    console.log(
      'Building job filtered PDF with invoiceIds:',
      invoiceIds,
      'creditMemoIds:',
      creditMemoIds
    );
    if (invoiceIds.length === 0) return null;

    const result = await dispatch(
      getJobFilteredStatementPdf({
        mainBuyerId,
        statementId: statementData.id,
        invoiceIds,
        creditMemoIds,
      })
    ).unwrap();

    return result?.data?.pdfUrl ?? null;
  }, [dispatch, mainBuyerId, filteredInvoices, filteredCreditMemos, statementData.id]);

  const handleDownloadJobFiltered = useCallback(async () => {
    console.log('handleDownloadJobFiltered');
    setLoadingJobPdfDownload(true);
    try {
      const blobUrl = await buildJobFilteredPdfUrl();
      if (!blobUrl) return;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `statement-${statementData.id}-jobs.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (err: any) {
      console.error('Failed to download job filtered statement', err);
      openDillToast({ type: 'ERROR', message: err?.message ?? 'Failed to download statement' });
    } finally {
      setLoadingJobPdfDownload(false);
    }
  }, [buildJobFilteredPdfUrl, statementData.id]);

  const handlePreviewJobFiltered = useCallback(async () => {
    setLoadingJobPdfPreview(true);
    try {
      const blobUrl = await buildJobFilteredPdfUrl();
      if (!blobUrl) return;
      setJobPdfBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return blobUrl;
      });
      setPdfPreviewOpen(true);
    } catch (err: any) {
      console.error('Failed to preview job filtered statement', err);
      openDillToast({ type: 'ERROR', message: err?.message ?? 'Failed to preview statement' });
    } finally {
      setLoadingJobPdfPreview(false);
    }
  }, [buildJobFilteredPdfUrl]);

  const handleToggleExpand = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('statementId') === statementData.id) {
          next.delete('statementId');
          setSelectedIds(new Set());
          setSearchText('');
          setSelectedJobIds([]);
        } else {
          next.set('statementId', statementData.id);
        }
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams, statementData.id]);

  const fetchStatementDetails = useCallback(() => {
    if (!mainBuyerId) return;
    setLoadingDetails(true);
    dispatch(getStatementDetails({ mainBuyerId, month: statementData.id }))
      .unwrap()
      .then((result) => {
        if (result?.success && result?.data) {
          const gi = result.data.groupedInvoices;
          if (gi) {
            const invoices: QuickbooksInvoice[] = [
              ...(gi.pastDueInvoices?.invoices ?? []),
              ...(gi.currentInvoices?.invoices ?? []),
              ...(gi.paidInvoices?.invoices ?? []),
              ...(gi.pendingPayInvoices?.invoices ?? []),
            ];
            const creditMemos: QuickbooksCreditMemo[] = [
              ...(gi.creditMemosDetails?.creditMemos ?? []),
              ...(gi.creditMemosDetails?.pendingCreditMemos ?? []),
            ];
            const pendingCmIds = (gi.creditMemosDetails?.pendingCreditMemos ?? []).map(
              (cm: any) => cm.id
            );
            setLiveInvoices(invoices);
            setLiveCreditMemos(creditMemos);
            setPendingCreditMemoIds(pendingCmIds);
            setGroupedInvoices(gi);

            // Auto-select all selectable items now that live data is available
            const selectableInvoiceIds = statementData.invoices
              .filter((inv) => {
                const liveInv = invoices.find((li) => li.id === inv.id);
                const isPaid = liveInv && liveInv.balance === 0;
                const isTransactionPending = liveInv?.dillPaymentStatus === 'TRANSACTION_PENDING';
                const isDeleted = liveInv?.isDeleted === true;
                return !isPaid && !isTransactionPending && !isDeleted;
              })
              .map((inv) => inv.id);
            const selectableCmIds = statementData.creditMemos
              .filter((cm) => {
                const liveCm = creditMemos.find((lcm) => lcm.id === cm.id);
                const isPending = pendingCmIds.includes(cm.id);
                const isUsed = !liveCm;
                const isTransactionPending = liveCm?.dillPaymentStatus === 'TRANSACTION_PENDING';
                return (
                  !isPending &&
                  !isUsed &&
                  !isTransactionPending &&
                  isCreditMemoSelectable(liveCm?.recordType, supplier?.integrationType)
                );
              })
              .map((cm) => cm.id);
            setSelectedIds(new Set([...selectableInvoiceIds, ...selectableCmIds]));
          }
        }
      })
      .catch((err) => {
        console.log('Failed to load statement details for expandable row', err);
      })
      .finally(() => {
        setLoadingDetails(false);
      });
  }, [dispatch, mainBuyerId, statementData.id]);

  // Fetch live data and pre-select items whenever this row becomes expanded
  // (via click or via ?statementId= in the URL on mount/refresh).
  useEffect(() => {
    if (!isExpanded) return;
    setSelectedIds(new Set(selectableIds));
    fetchStatementDetails();
    // selectableIds is intentionally excluded — we only want to (re)fetch when
    // this row's expansion state toggles, not when liveCreditMemos updates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, fetchStatementDetails]);

  const selectedNetTotal = useMemo(() => {
    const selectedInvoiceTotal = statementData.invoices
      .filter((inv) => selectedIds.has(inv.id))
      .reduce((sum, inv) => {
        const liveInv = liveInvoices.find((li) => li.id === inv.id);
        let currentBal = liveInv?.balance ?? inv.balance ?? 0;
        const amountPaid =
          (liveInv?.dillInvoicePaymentSummary?.amountPaid ?? 0) +
          (liveInv?.dillInvoicePaymentSummary?.discountAmount ?? 0);
        if (liveInv?.dillInvoicePaymentSummary && currentBal !== 0) {
          currentBal = currentBal - amountPaid;
        }
        return sum + currentBal;
      }, 0);
    const selectableCmIdSet = new Set(
      statementData.creditMemos
        .filter((cm) => {
          const liveCm = liveCreditMemos.find((lcm) => lcm.id === cm.id);
          const isUsed = !liveCm;
          const isTransactionPending = liveCm?.dillPaymentStatus === 'TRANSACTION_PENDING';
          return (
            isCreditMemoSelectable(liveCm?.recordType, supplier?.integrationType) &&
            !isTransactionPending &&
            !isUsed
          );
        })
        .map((cm) => cm.id)
    );
    const selectedCreditTotal = statementData.creditMemos
      .filter((cm) => selectedIds.has(cm.id) && selectableCmIdSet.has(cm.id))
      .reduce((sum, cm) => sum + (cm.remainingCredit ?? 0), 0);
    return selectedInvoiceTotal - selectedCreditTotal;
  }, [statementData, selectedIds, supplier?.integrationType, liveCreditMemos, liveInvoices]);

  const totalPendingTransactionsAmount = useMemo(() => {
    const totalCurrentPaidPendingInvoices = liveInvoices
      ? filteredInvoices.reduce((sum, inv) => {
          const current = liveInvoices?.find((li) => li.id === inv.id);
          if (current?.isDeleted === true) return sum;
          const amountPaid =
            (current?.dillInvoicePaymentSummary?.amountPaid ?? 0) +
            (current?.dillInvoicePaymentSummary?.discountAmount ?? 0);

          return sum + amountPaid;
        }, 0)
      : 0;

    const totalCurrentPaidPendingCredit = liveCreditMemos
      ? filteredCreditMemos.reduce((sum, cm) => {
          const current = liveCreditMemos?.find((lcm) => lcm.id === cm.id);
          const isPending = pendingCreditMemoIds?.includes(cm.id);
          if (!isPending) {
            return sum;
          }
          return sum + (current?.remainingCredit ?? cm.remainingCredit ?? 0);
        }, 0)
      : 0;
    return totalCurrentPaidPendingInvoices - totalCurrentPaidPendingCredit;
  }, [liveCreditMemos, liveInvoices, filteredCreditMemos, filteredInvoices]);

  const totalCurrentOpenAmount = useMemo(() => {
    const totalCurrentBalanceInvoices = liveInvoices
      ? filteredInvoices.reduce((sum, inv) => {
          const current = liveInvoices?.find((li) => li.id === inv.id);
          if (current?.isDeleted === true) return sum;
          const currentBal = current?.balance ?? inv.balance ?? 0;

          return sum + currentBal;
        }, 0)
      : 0;
    const totalCurrentCredits = liveCreditMemos
      ? filteredCreditMemos.reduce((sum, cm) => {
          const current = liveCreditMemos?.find((lcm) => lcm.id === cm.id);

          return sum + (current?.remainingCredit ?? cm.remainingCredit ?? 0);
        }, 0)
      : 0;
    return totalCurrentBalanceInvoices - totalCurrentCredits;
  }, [liveCreditMemos, liveInvoices, filteredCreditMemos, filteredInvoices]);

  const totalCurrentBalance = useMemo(() => {
    const totalCurrentBalanceInvoices = liveInvoices
      ? filteredInvoices.reduce((sum, inv) => {
          const current = liveInvoices?.find((li) => li.id === inv.id);
          if (current?.isDeleted === true) return sum;
          let currentBal = current?.balance ?? inv.balance ?? 0;
          const amountPaid =
            (current?.dillInvoicePaymentSummary?.amountPaid ?? 0) +
            (current?.dillInvoicePaymentSummary?.discountAmount ?? 0);
          if (current?.dillInvoicePaymentSummary && currentBal !== 0) {
            currentBal = currentBal - amountPaid;
          }
          return sum + currentBal;
        }, 0)
      : 0;
    const totalCurrentCredits = liveCreditMemos
      ? filteredCreditMemos.reduce((sum, cm) => {
          const current = liveCreditMemos?.find((lcm) => lcm.id === cm.id);
          const isPending = pendingCreditMemoIds?.includes(cm.id);
          const isUsed = !current;
          const isTransactionPending = current?.dillPaymentStatus === 'TRANSACTION_PENDING';
          if (isPending || isUsed || isTransactionPending) {
            return sum;
          }
          return sum + (current?.remainingCredit ?? cm.remainingCredit ?? 0);
        }, 0)
      : 0;
    return totalCurrentBalanceInvoices - totalCurrentCredits;
  }, [liveCreditMemos, liveInvoices, filteredCreditMemos, filteredInvoices]);

  const jobLabel = activeJobCount === 1 ? 'Job' : 'Jobs';

  const pdfPreviewUrl =
    activeJobCount > 0 && jobPdfBlobUrl ? jobPdfBlobUrl : statementFileUrl ?? '';

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 ${
        isExpanded ? '' : 'overflow-hidden'
      }`}>
      {pdfPreviewOpen && pdfPreviewUrl && (
        <AppPdfPreviewModal
          open={pdfPreviewOpen}
          handleClose={handleClosePdfPreview}
          invoice={`Statement ${formattedDate}`}
          pdfType="STATEMENT"
          pdfList={[
            {
              name: `Statement ${formattedDate}`,
              url: pdfPreviewUrl,
              date: new Date(statementData.statementDate),
            },
          ]}
          handleDownloadPdf={handleDownloadStatementPdf}
          mode="Buyer"
          showHistoryButton={false}
        />
      )}
      {payInvoicesModalOpen && (
        <AppSelectedPayInvoicesModal
          open={payInvoicesModalOpen}
          title="Pay Statement"
          handleClose={() => {
            setInvoicesToBePaid([]);
            setCreditMemosToApply([]);
            setPayInvoicesModalOpen(false);
          }}
          selectedInvoices={invoicesToBePaid}
          paymentTo={supplier?.name?.toLocaleLowerCase()}
          paymentFrom={selectedMainBuyer?.name?.toLocaleLowerCase()}
          selectedCreditMemos={creditMemosToApply}
          onPaymentSuccess={fetchStatementDetails}
        />
      )}
      {/* Sticky header — includes job filter row when expanded */}
      <div className={`bg-white z-10 ${isExpanded ? 'sticky top-0 rounded-[12px]' : ''}`}>
        {/* Job filter row — sticky with the header */}
        {isExpanded && selectedSupplierJobs.length > 0 && (
          <div className="px-4 md:px-6 border-t border-gray-100 pt-4 flex items-center gap-2 flex-wrap">
            <div className="relative z-[60] w-full sm:w-auto sm:min-w-[160px]">
              <DillDropdown
                placeholder=""
                inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
                customViewElement={
                  <div className="flex my-1 px-2 items-center">
                    <Buildings size={16} className="text-blue-500" />
                    <p className="text-sm ml-2 text-gray-600">Jobs Filter</p>
                  </div>
                }
                isSearchable={true}
                isCreatable={false}
                closeMenu={false}
                itemWidth={300}
                items={[{ jobName: 'All Jobs', id: 'ALL' }, ...selectedSupplierJobs].map((job) => {
                  const selectedJob = selectedSupplierJobs.find((j) => j.id === job.id);
                  const jobAddress = `${selectedJob?.address ?? ''} ${selectedJob?.city ?? ''} ${
                    selectedJob?.state ? `${selectedJob?.state},` : ''
                  } ${selectedJob?.zipCode ?? ''}`.trim();
                  const jobName = `${
                    selectedJob?.jobNumber && selectedJob.jobNumber !== job.jobName
                      ? `${selectedJob.jobNumber} - `
                      : ''
                  }${job.jobName}`;
                  return {
                    label: jobName,
                    value: job.id,
                    labelInElement: (
                      <div className="flex m-1 items-center gap-1">
                        <DillCheckbox isChecked={selectedJobIds?.includes(job.id)} />
                        <Buildings size={16} className="text-blue-500 flex-shrink-0" />
                        <div className="flex-1 truncate">
                          <div className="text-black text-xs truncate">{jobName}</div>
                          {jobAddress !== '' && (
                            <div className="text-gray-500 text-2xs truncate">
                              {truncateText(jobAddress, 35)}
                            </div>
                          )}
                        </div>
                      </div>
                    ),
                  };
                })}
                isMulti
                onMultiSelectChange={(data, oldMultiValue) => {
                  if (data) {
                    const valueList = data.map((item) => item.value);
                    const valueListWithoutAll = valueList.filter((item) => item !== 'ALL');
                    if (
                      !valueList.includes('ALL') &&
                      oldMultiValue &&
                      oldMultiValue.includes('ALL') &&
                      valueListWithoutAll.length === selectedSupplierJobs.length
                    ) {
                      setSelectedJobIds([]);
                    } else if (
                      valueList.includes('ALL') &&
                      valueListWithoutAll.length < selectedSupplierJobs.length &&
                      oldMultiValue &&
                      oldMultiValue.includes('ALL')
                    ) {
                      setSelectedJobIds(valueListWithoutAll);
                    } else if (
                      (valueList.includes('ALL') &&
                        valueListWithoutAll.length === 0 &&
                        oldMultiValue &&
                        !oldMultiValue.includes('ALL')) ||
                      valueList.includes('ALL') ||
                      (valueListWithoutAll.length === selectedSupplierJobs.length &&
                        !valueList.includes('ALL'))
                    ) {
                      setSelectedJobIds(['ALL', ...selectedSupplierJobs.map((item) => item.id)]);
                    } else if (valueList.length > 0) {
                      setSelectedJobIds(valueList);
                    } else {
                      setSelectedJobIds([]);
                    }
                  }
                }}
                value={null}
                multiValue={selectedJobIds}
                menuAlign="left"
              />
              {activeJobCount > 0 && (
                <div className="flex absolute bottom-6 right-[-12px] bg-blue-500 text-white rounded-full text-xs font-bold h-5 w-5 items-center justify-center">
                  {activeJobCount}
                </div>
              )}
            </div>
            {activeJobCount > 0 && (
              <>
                {activeJobs.slice(0, 2).map((job) => {
                  const chipLabel = `${job.jobNumber ? `${job.jobNumber} - ` : ''}${job.jobName}`;
                  return (
                    <span
                      key={job.id}
                      className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 max-w-[200px]">
                      <Buildings size={10} className="flex-shrink-0" />
                      <span className="truncate">{chipLabel}</span>
                      <button
                        className="flex-shrink-0 flex items-center justify-center rounded-full hover:bg-red-200 p-0.5 cursor-pointer bg-transparent border-none text-red-500"
                        onClick={() => {
                          setSelectedJobIds(
                            selectedJobIds.filter((id) => id !== job.id && id !== 'ALL')
                          );
                        }}>
                        <X size={10} />
                      </button>
                    </span>
                  );
                })}
                {activeJobs.length > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200">
                    +{activeJobs.length - 2}
                  </span>
                )}
                <button
                  className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer bg-transparent border-none"
                  onClick={() => setSelectedJobIds([])}>
                  Clear All
                </button>
              </>
            )}
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 md:px-6 md:py-4 gap-3">
          {/* ── Info row ── */}
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <button
              onClick={handleToggleExpand}
              className={`flex items-center justify-center h-9 w-9 rounded-lg shrink-0 transition-colors duration-200 ${
                isExpanded ? 'bg-blue-100 hover:bg-blue-200' : 'bg-gray-100 hover:bg-gray-200'
              }`}>
              <CaretDown
                size={16}
                className={`transition-all duration-200 ${
                  isExpanded ? 'text-blue-600 rotate-0' : 'text-gray-400 -rotate-90'
                }`}
                weight="bold"
              />
            </button>
            <div
              className={`min-w-0 flex-1 ${!isExpanded ? 'cursor-pointer' : ''}`}
              onClick={!isExpanded ? handleToggleExpand : undefined}>
              <p
                className={`text-sm font-semibold transition-colors duration-200 ${
                  isExpanded ? 'text-blue-600' : 'text-gray-900'
                }`}>
                Statement: {formattedDate}
              </p>
              {/* Balance pills — two columns on mobile */}
              <div className="flex items-center gap-4 mt-1">
                <div className="flex gap-2">
                  <span className="text-xs text-gray-400 font-medium">Original Balance</span>
                  <span className="text-xs font-semibold text-gray-700 tabular-nums">
                    {formatMoney(filteredOriginalBalance, 2)}
                  </span>
                </div>
                {isExpanded && (
                  <div className="flex gap-2">
                    <span className="text-xs  text-gray-400 font-medium">Remaining Balance</span>
                    {loadingDetails ? (
                      <Spinner size={12} className="animate-spin text-gray-400 mt-0.5" />
                    ) : (
                      <span className="text-xs font-semibold text-gray-700 tabular-nums">
                        {liveInvoices.length > 0 ? formatMoney(totalCurrentBalance, 2) : '—'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {isExpanded &&
                totalPendingTransactionsAmount > 0 &&
                liveInvoices.length > 0 &&
                selectedIds.size === selectableIds.length &&
                selectedNetTotal !== totalCurrentBalance && (
                  <div className="flex items-start gap-1.5 mt-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded-md max-w-max">
                    <Info size={12} className="text-amber-500 shrink-0 mt-px" weight="fill" />
                    <p className="text-[10px] text-amber-700 leading-tight">
                      Pay Selected does not match Remaining Balance because you have at least one
                      open invoice linked to a pending transaction.
                    </p>
                  </div>
                )}
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="flex flex-col gap-2 shrink-0 sm:items-end">
            {/* Pay Selected row */}
            {selectedIds.size > 0 && isExpanded && (
              <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto bg-blue-50 sm:bg-transparent rounded-xl px-3 py-2 sm:p-0">
                <span className="text-xs text-gray-600 sm:text-gray-500">
                  <span className="font-semibold text-gray-800">{selectedIds.size}</span>
                  {' selected · '}
                  <span className="font-semibold text-gray-800 tabular-nums">
                    {formatMoney(selectedNetTotal, 2)}
                  </span>
                </span>
                <div data-tooltip-id={!buyerUserCanPay ? 'no_pay_permission' : undefined}>
                  <DillButton
                    onClick={handlePayClick}
                    label="Pay Selected"
                    variant="primary"
                    isDisabled={!buyerUserCanPay}
                  />
                </div>
              </div>
            )}
            {/* Download + Preview row */}
            <div className="flex gap-2 w-full sm:w-auto">
              {activeJobCount > 0 ? (
                <div className="flex-1 sm:flex-none">
                  <DillButton
                    onClick={handleDownloadJobFiltered}
                    variant="secondary"
                    label={`Download ${jobLabel} Statement`}
                    icon={<DownloadSimple size={16} />}
                    isLoading={loadingJobPdfDownload}
                  />
                </div>
              ) : (
                <DillButton
                  onClick={() => handleDownloadStatementPdf(statementFileUrl ?? '')}
                  variant="secondary"
                  label={`Download Statement`}
                  isDisabled={!statementFileUrl}
                  icon={<DownloadSimple size={18} className="text-gray-400" />}
                />
              )}
              {activeJobCount > 0 ? (
                <div className="flex-1 sm:flex-none">
                  <DillButton
                    onClick={handlePreviewJobFiltered}
                    variant="secondary"
                    label={`Preview ${jobLabel} Statement`}
                    icon={<Eye size={16} />}
                    isLoading={loadingJobPdfPreview}
                  />
                </div>
              ) : (
                <DillButton
                  onClick={handleOpenPdfPreview}
                  variant="secondary"
                  label={`Preview Statement`}
                  isDisabled={!statementFileUrl}
                  icon={<Eye size={18} className="text-gray-400" />}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-gray-100 relative">
          {(liveInvoices.length > 0 || liveCreditMemos.length > 0) && (
            <div className="px-6 pt-3 pb-2 flex justify-between md:flex-wrap gap-2">
              <div className="flex z-40 items-center flex-wrap lg:flex-nowrap w-full gap-3">
                <div className="w-full sm:w-auto sm:min-w-[180px] lg:w-[250px]">
                  <AppInputField
                    inputFieldStyles={{ padding: '6px 6px' }}
                    id="search"
                    placeholder="Search"
                    icon={<Search size={13} color={COLORS.GREY_500} />}
                    onTextChange={(text) => {
                      setSearchText(text);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <StatementExpandableTable
            invoices={filteredInvoices}
            creditMemos={filteredCreditMemos}
            liveInvoices={filteredLiveInvoices}
            liveCreditMemos={filteredLiveCreditMemos}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            integrationType={supplier?.integrationType}
            pendingCreditMemoIds={pendingCreditMemoIds}
            totalPendingTransactionsAmount={totalPendingTransactionsAmount}
            totalCurrentBalance={totalCurrentBalance}
          />
          {loadingDetails && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm rounded-b-xl z-10">
              <div className="flex items-center gap-2 text-gray-400">
                <Spinner size={20} className="animate-spin" />
                <span className="text-xs">Loading details...</span>
              </div>
            </div>
          )}
        </div>
      )}
      <ReactTooltip
        id={'no_pay_permission'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '300px',
          zIndex: 99999,
        }}>
        <span>You don't have permissions to make payments. Please contact your account admin.</span>
      </ReactTooltip>
    </div>
  );
};

export default StatementPeriodRow;
